import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import { Calendar as CalendarIcon, Clock, Users, Check, Phone, Mail } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import { Calendar } from '../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { toast } from 'sonner';
import { format } from 'date-fns';
import { fr, enUS, pt } from 'date-fns/locale';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Reservations = () => {
  const { t, language, getLocalizedField } = useLanguage();
  const [searchParams] = useSearchParams();
  const [date, setDate] = useState(null);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [settings, setSettings] = useState(null);
  const [siteContent, setSiteContent] = useState(null);
  const [timeSlots, setTimeSlots] = useState([]);
  const [dateBlocked, setDateBlocked] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    time: '',
    guests: 2,
    notes: '',
    seating: 'interior'
  });

  const locales = { fr, en: enUS, pt };

  useEffect(() => {
    axios.get(`${API}/settings`).then(res => {
      setSettings(res.data);
    }).catch(() => {});
    axios.get(`${API}/content`).then(res => {
      setSiteContent(res.data);
    }).catch(() => {});
  }, []);

  // Pre-fill from query params (e.g. from event popup CTA: /reservations?date=YYYY-MM-DD&time=HH:MM&notes=Brunch)
  useEffect(() => {
    const qDate = searchParams.get('date');
    const qTime = searchParams.get('time');
    const qNotes = searchParams.get('notes');
    if (qDate) {
      try {
        const d = new Date(qDate + 'T00:00:00');
        if (!isNaN(d.getTime())) setDate(d);
      } catch { /* ignore */ }
    }
    if (qTime || qNotes) {
      setFormData((prev) => ({
        ...prev,
        time: qTime || prev.time,
        notes: qNotes || prev.notes,
      }));
    }
  }, [searchParams]);

  // Fallback texts
  const fallback = {
    fr: { reservations_title: 'Réservations', reservations_subtitle: 'Réservez votre table', reservations_success_text: 'Nous vous contacterons pour confirmer votre réservation.', reservations_closed_message: 'Nous ne prenons pas de réservations pour le moment.', reservations_contact_text: 'Pour plus d\'informations, n\'hésitez pas à nous contacter :', reservations_dateblocked: 'Ce jour-là, nous ne prenons pas de réservations.', reservations_info_title: 'Informations Utiles', reservations_info_hours: 'Horaires', reservations_info_contact: 'Contact', reservations_info_address: 'Adresse' },
    en: { reservations_title: 'Reservations', reservations_subtitle: 'Book your table', reservations_success_text: 'We will contact you to confirm your reservation.', reservations_closed_message: 'We are not taking reservations at this time.', reservations_contact_text: 'For more information, please contact us:', reservations_dateblocked: "We don't take reservations on this day.", reservations_info_title: 'Useful Information', reservations_info_hours: 'Hours', reservations_info_contact: 'Contact', reservations_info_address: 'Address' },
    pt: { reservations_title: 'Reservas', reservations_subtitle: 'Reserve sua mesa', reservations_success_text: 'Iremos contactá-lo para confirmar a sua reserva.', reservations_closed_message: 'Não estamos a aceitar reservas neste momento.', reservations_contact_text: 'Para mais informações, contacte-nos:', reservations_dateblocked: 'Não aceitamos reservas neste dia.', reservations_info_title: 'Informações Úteis', reservations_info_hours: 'Horários', reservations_info_contact: 'Contacto', reservations_info_address: 'Endereço' }
  };
  const txt = (field) => {
    if (siteContent) {
      const v = getLocalizedField(siteContent, field);
      if (v) return v;
    }
    return fallback[language]?.[field] || fallback.fr[field] || '';
  };

  // Generate available time slots from settings ranges (15-min intervals)
  const generateTimeSlots = useCallback((ranges) => {
    if (!ranges || ranges.length === 0) {
      return ['11:30','11:45','12:00','12:15','12:30','12:45','13:00','13:15','13:30',
              '18:00','18:15','18:30','18:45','19:00','19:15','19:30','19:45','20:00','20:15','20:30'];
    }
    const slots = [];
    ranges.forEach(range => {
      const [startH, startM] = range.start.split(':').map(Number);
      const [endH, endM] = range.end.split(':').map(Number);
      let h = startH, m = startM;
      while (h < endH || (h === endH && m <= endM)) {
        slots.push(`${String(h).padStart(2,'0')}:${String(m).padStart(2,'0')}`);
        m += 15;
        if (m >= 60) { h++; m = m - 60; }
      }
    });
    return slots;
  }, []);

  // Check if a date is blocked
  const isDateDisabled = useCallback((d) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (d < today) return true;

    if (!settings) return d.getDay() === 0;

    // Closed weekdays (0=Sunday, 1=Monday, etc.)
    const closedDays = settings.reservation_closed_weekdays || [0];
    if (closedDays.includes(d.getDay())) return true;

    // Blocked specific dates
    const blocked = settings.reservation_blocked_dates || [];
    const dateStr = format(d, 'yyyy-MM-dd');
    if (blocked.includes(dateStr)) return true;

    return false;
  }, [settings]);

  // When date changes, check if blocked and update time slots
  useEffect(() => {
    if (!date || !settings) {
      setDateBlocked(false);
      return;
    }
    const dateStr = format(date, 'yyyy-MM-dd');
    const blocked = settings.reservation_blocked_dates || [];
    const closedDays = settings.reservation_closed_weekdays || [0];

    if (blocked.includes(dateStr) || closedDays.includes(date.getDay())) {
      setDateBlocked(true);
      setTimeSlots([]);
    } else {
      setDateBlocked(false);
      // Prefer per-day hours if configured for this weekday, otherwise fall back to global slots
      const perDay = settings.reservation_hours_by_day || {};
      const dayKey = String(date.getDay()); // 0=Sun..6=Sat
      const slotsForDay = (perDay[dayKey] && perDay[dayKey].length > 0)
        ? perDay[dayKey]
        : (settings.reservation_time_slots || []);
      setTimeSlots(generateTimeSlots(slotsForDay));
    }
  }, [date, settings, generateTimeSlots]);

  // Initial time slots (no date selected — show global default if available)
  useEffect(() => {
    if (settings && !date) {
      setTimeSlots(generateTimeSlots(settings.reservation_time_slots));
    }
  }, [settings, generateTimeSlots, date]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!date || !formData.time || !formData.name || !formData.email || !formData.phone) {
      toast.error('Veuillez remplir tous les champs obligatoires');
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/reservations`, {
        ...formData,
        date: format(date, 'yyyy-MM-dd'),
        guests: parseInt(formData.guests)
      });
      setSuccess(true);
      toast.success(t('reservations_success'));
    } catch (error) {
      const detail = error.response?.data?.detail;
      if (error.response?.status === 403) {
        toast.error('Les réservations sont actuellement fermées.');
      } else if (error.response?.status === 409 && detail && typeof detail === 'object') {
        const msgs = {
          terrace_closed: {
            fr: settings?.terrace_closed_message_fr,
            en: settings?.terrace_closed_message_en,
            pt: settings?.terrace_closed_message_pt,
          },
        };
        const msg = (msgs[detail.code]?.[language]) || detail.message || t('reservations_error');
        toast.error(msg);
      } else if (error.response?.status === 409 && typeof detail === 'string') {
        toast.error(detail);
      } else {
        toast.error(t('reservations_error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const reservationsEnabled = settings?.reservations_enabled ?? true;

  if (success) {
    return (
      <main className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center" data-testid="reservations-success">
        <motion.div initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} className="text-center px-6">
          <div className="w-20 h-20 bg-[#d4af37] rounded-full flex items-center justify-center mx-auto mb-8">
            <Check className="w-10 h-10 text-black" />
          </div>
          <h2 className="font-display text-4xl text-white mb-4" data-testid="success-title">
            {t('reservations_success')}
          </h2>
          <p className="text-white/60 mb-2">
            {format(date, 'EEEE d MMMM yyyy', { locale: locales[language] })}
          </p>
          <p className="text-[#d4af37] text-xl mb-8">{formData.time}</p>
          <p className="text-white/50 text-sm max-w-md mx-auto">
            {txt('reservations_success_text')}
          </p>
        </motion.div>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-[#0a0a0a] pt-20" data-testid="reservations-page">
      {/* Hero */}
      <section className="relative py-24 md:py-32 px-6 md:px-12">
        <div className="absolute inset-0 overflow-hidden">
          <img src="https://images.unsplash.com/photo-1559339352-11d035aa65de?w=1920&q=80" alt="Restaurant interior" className="w-full h-full object-cover opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/80 to-[#0a0a0a]" />
        </div>
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }}>
            <p className="text-xs uppercase tracking-[0.3em] text-[#d4af37] mb-4">{txt('reservations_subtitle')}</p>
            <h1 className="font-display text-5xl md:text-7xl text-white" data-testid="reservations-title">{txt('reservations_title')}</h1>
          </motion.div>
        </div>
      </section>

      {/* Reservation Form */}
      <section className="py-16 px-6 md:px-12">
        <div className="max-w-3xl mx-auto">
          {!reservationsEnabled ? (
            <div className="text-center py-12 space-y-4" data-testid="reservations-closed">
              <p className="text-white/70 text-lg">
                {txt('reservations_closed_message')}
              </p>
              <p className="text-white/50 text-sm">
                {txt('reservations_contact_text')}
              </p>
              <div className="flex flex-col items-center gap-2 mt-4">
                <a href={`tel:${settings?.phone || '+352 661 250 004'}`} className="flex items-center gap-2 text-[#d4af37] hover:underline">
                  <Phone className="w-4 h-4" /> {settings?.phone || '+352 661 250 004'}
                </a>
                <a href={`mailto:${settings?.email || 'beniluxembourg@gmail.com'}`} className="flex items-center gap-2 text-[#d4af37] hover:underline">
                  <Mail className="w-4 h-4" /> {settings?.email || 'beniluxembourg@gmail.com'}
                </a>
              </div>
            </div>
          ) : (
          <motion.form
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="space-y-8"
            data-testid="reservation-form"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* Date Picker */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-white/70">
                  {t('reservations_date')} *
                </Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      className="w-full justify-start text-left bg-transparent border-white/20 hover:border-[#d4af37] hover:bg-white/5 text-white h-14"
                      data-testid="date-picker-trigger"
                    >
                      <CalendarIcon className="mr-3 h-4 w-4 text-[#d4af37]" />
                      {date ? (
                        format(date, 'PPP', { locale: locales[language] })
                      ) : (
                        <span className="text-white/50">Choisir une date</span>
                      )}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0 bg-[#121212] border-white/10" align="start">
                    <Calendar
                      mode="single"
                      selected={date}
                      onSelect={setDate}
                      disabled={isDateDisabled}
                      locale={locales[language]}
                      className="bg-[#121212]"
                      data-testid="date-calendar"
                    />
                  </PopoverContent>
                </Popover>
              </div>

              {/* Time Selector */}
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-white/70">
                  {t('reservations_time')} *
                </Label>
                <div className="relative">
                  <Clock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#d4af37]" />
                  <select
                    name="time"
                    value={formData.time}
                    onChange={handleChange}
                    className="w-full h-14 pl-12 pr-4 bg-transparent border border-white/20 text-white appearance-none cursor-pointer hover:border-[#d4af37] focus:border-[#d4af37] focus:outline-none transition-colors"
                    data-testid="time-select"
                  >
                    <option value="" className="bg-[#121212]">Choisir une heure</option>
                    {timeSlots.map(time => (
                      <option key={time} value={time} className="bg-[#121212]">{time}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>

            {/* Date blocked message */}
            {dateBlocked && date && (
              <div className="bg-[#1a1a1a] border border-[#d4af37]/30 p-4 rounded text-center space-y-2" data-testid="date-blocked-message">
                <p className="text-white/70">
                  {txt('reservations_dateblocked')}
                </p>
                <p className="text-white/50 text-sm">
                  {txt('reservations_contact_text')}
                </p>
                <div className="flex justify-center gap-6 mt-2">
                  <a href={`tel:${settings?.phone || '+352 661 250 004'}`} className="flex items-center gap-1 text-[#d4af37] text-sm hover:underline">
                    <Phone className="w-3 h-3" /> {settings?.phone || '+352 661 250 004'}
                  </a>
                  <a href={`mailto:${settings?.email || 'beniluxembourg@gmail.com'}`} className="flex items-center gap-1 text-[#d4af37] text-sm hover:underline">
                    <Mail className="w-3 h-3" /> {settings?.email || 'beniluxembourg@gmail.com'}
                  </a>
                </div>
              </div>
            )}

            {/* Guests */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-white/70">
                {t('reservations_guests')} *
              </Label>
              <div className="relative">
                <Users className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#d4af37]" />
                <select
                  name="guests"
                  value={formData.guests}
                  onChange={handleChange}
                  className="w-full h-14 pl-12 pr-4 bg-transparent border border-white/20 text-white appearance-none cursor-pointer hover:border-[#d4af37] focus:border-[#d4af37] focus:outline-none transition-colors"
                  data-testid="guests-select"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(num => (
                    <option key={num} value={num} className="bg-[#121212]">
                      {num} {num === 1 ? 'personne' : 'personnes'}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Contact Info */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-white/70">{t('reservations_name')} *</Label>
                <Input name="name" value={formData.name} onChange={handleChange} className="bg-transparent border-white/20 hover:border-[#d4af37] text-white h-14" placeholder="Votre nom" data-testid="name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-white/70">{t('reservations_email')} *</Label>
                <Input name="email" type="email" value={formData.email} onChange={handleChange} className="bg-transparent border-white/20 hover:border-[#d4af37] text-white h-14" placeholder="email@exemple.com" data-testid="email-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.15em] text-white/70">{t('reservations_phone')} *</Label>
                <Input name="phone" value={formData.phone} onChange={handleChange} className="bg-transparent border-white/20 hover:border-[#d4af37] text-white h-14" placeholder="+352 ..." data-testid="phone-input" />
              </div>
            </div>

            {/* Seating choice */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-white/70">
                {language === 'fr' ? 'Préférence' : language === 'pt' ? 'Preferência' : 'Preference'}
              </Label>
              <div className="grid grid-cols-2 gap-2" data-testid="seating-toggle">
                {[
                  { id: 'interior', label_fr: 'Intérieur', label_en: 'Indoor', label_pt: 'Interior' },
                  { id: 'terrace', label_fr: 'Terrasse', label_en: 'Terrace', label_pt: 'Esplanada' },
                ].map((opt) => {
                  const active = formData.seating === opt.id;
                  return (
                    <button
                      type="button"
                      key={opt.id}
                      onClick={() => setFormData((p) => ({ ...p, seating: opt.id }))}
                      data-testid={`seating-${opt.id}`}
                      className={`h-12 rounded border text-sm tracking-wide transition active:scale-[0.99] ${
                        active
                          ? 'bg-[#d4af37] text-black border-[#d4af37] font-medium'
                          : 'bg-transparent text-white/80 border-white/20 hover:border-[#d4af37]/60'
                      }`}
                    >
                      {opt[`label_${language}`] || opt.label_fr}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Notes */}
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-[0.15em] text-white/70">{t('reservations_notes')}</Label>
              <Textarea name="notes" value={formData.notes} onChange={handleChange} className="bg-transparent border-white/20 hover:border-[#d4af37] text-white resize-none" placeholder="Demandes spéciales, allergies..." rows={3} data-testid="notes-textarea" />
            </div>

            <Button type="submit" disabled={loading || dateBlocked} className="w-full h-14 bg-[#d4af37] text-black font-medium text-lg hover:bg-white transition-colors" data-testid="submit-reservation">
              {loading ? t('loading') : t('reservations_submit')}
            </Button>
          </motion.form>
          )}
        </div>
      </section>

      {/* Info Section */}
      <section className="py-16 px-6 md:px-12 border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center space-y-6">
          <h2 className="font-display text-3xl text-white">{txt('reservations_info_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 text-white/60">
            <div>
              <p className="text-[#d4af37] text-sm uppercase tracking-wider mb-2">{txt('reservations_info_hours')}</p>
              <p className="text-sm">{settings?.hours_fr || 'Lundi à Samedi 11h30-14h30'}</p>
              <p className="text-sm">Jeudi à Samedi 17h00-22h00</p>
            </div>
            <div>
              <p className="text-[#d4af37] text-sm uppercase tracking-wider mb-2">{txt('reservations_info_contact')}</p>
              <p className="text-sm">{settings?.phone || '+352 661 250 004'}</p>
              <p className="text-sm">{settings?.email || 'beniluxembourg@gmail.com'}</p>
            </div>
            <div>
              <p className="text-[#d4af37] text-sm uppercase tracking-wider mb-2">{txt('reservations_info_address')}</p>
              <p className="text-sm">{settings?.address || '22 Rue de l\'industrie'}</p>
              <p className="text-sm">L 8399 Windhof</p>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
};

export default Reservations;
